import * as Yup from "yup";
import { TReactSetState, TUseTranslationTfn } from "../../data/AppType";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
} from "../../utils/AppFunctions";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  CREATE_EXPENSE,
  GET_EXPENSES_BY_ID,
  GLOBAL_DROP_DOWN,
  UPDATE_EXPENSE,
} from "../../services/Endpoints";
import { LOOKUP_CATEGORY, PAYMENT_METHOD } from "../../utils/AppEnumerations";
import { IOption } from "../../data/AppInterface";

export interface IExpensesForm {
  id?: string;
  title: string;
  expense_head: string;
  expense_date?: string;
  amount?: number;
  payment_method: string;
  transaction_reference?: string;
  receipt_media_url?: string;
  description?: string;
  created_by?: string;
  created_date?: string;
  modified_by?: string;
  modified_date?: string;
}

export const EXPENSES_FIELD_INITIAL_VALUES = {
  title: "",
  expense_head: "",
  expense_date: undefined,
  amount: undefined,
  payment_method: "",
  transaction_reference: undefined,
  receipt_media_url: undefined,
  description: undefined,
};

export const expensesFieldValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    title: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("title")],
        ])
      )
      .max(
        50,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("title")],
          ["max", "50"],
        ])
      )
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("title")],
          ["min", "2"],
        ])
      ),
    expense_head: Yup.string().required(
      prepareMessageFromParams(t("error_message_required"), [
        ["fieldName", t("expense_head")],
      ])
    ),
    expense_date: Yup.string().required(
      prepareMessageFromParams(t("error_message_required"), [
        ["fieldName", t("expense_date")],
      ])
    ),
    payment_method: Yup.string().required(
      prepareMessageFromParams(t("error_message_required"), [
        ["fieldName", t("expense_date")],
      ])
    ),
    amount: Yup.number()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("amount")],
        ])
      )
      .max(
        9999999999.99,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("amount")],
          ["max", "9999999999.99"],
        ])
      )
      .min(
        10,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("amount")],
          ["min", "10"],
        ])
      ),
    transaction_reference: Yup.string()
      .nullable()
      .when("payment_method", {
        is: (payment_method: PAYMENT_METHOD) =>
          payment_method !== PAYMENT_METHOD.Cash,
        then: (schema) =>
          schema.required(
            prepareMessageFromParams(t("error_message_required"), [
              ["fieldName", t("transaction_reference_no")],
            ])
          ),
        otherwise: (schema) => schema.nullable(),
      })
      .max(
        100,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("transaction_reference_no")],
          ["max", "100"],
        ])
      )
      .min(
        1,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("transaction_reference_no")],
          ["min", "1"],
        ])
      ),
    description: Yup.string()
      .max(
        100,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("description")],
          ["max", "100"],
        ])
      )
      .min(
        1,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("description")],
          ["min", "1"],
        ])
      ),
  });

export const fetchExpenseFieldById = async (
  id: string,
  handleClose: (status: boolean) => void
) => {
  try {
    const expensesData = await GET_EXPENSES_BY_ID(id);
    if (expensesData && expensesData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      return {
        ...expensesData.data,
        description: expensesData.data.description ?? undefined,
      };
    } else {
      handleClose(false);
      sweetAlertError({
        message: expensesData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleClose(false);
    handleError(e, "fetchExpensesId");
  }
};
export const handleSubmitExpenses = async (
  values: IExpensesForm,
  setLoading: TReactSetState<boolean>,
  handleClose: (status: boolean) => void,
  id: string | undefined
) => {
  setLoading(true);
  try {
    let resultExpenses;
    if (id) {
      resultExpenses = await UPDATE_EXPENSE(values, id);
    } else {
      resultExpenses = await CREATE_EXPENSE(values);
    }
    if (resultExpenses && resultExpenses.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({
        message: resultExpenses.message,
      });
      return handleClose(true);
    } else {
      sweetAlertError({
        message: resultExpenses?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    sweetAlertError(e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    handleError(e, "handleSubmitExpenses");
  } finally {
    setLoading(false);
  }
};

export const getExpenseHeadDdl = async (
  setExpenseHead: TReactSetState<IOption<string>[]>,
  selectedExpenseHeadValue?: string
) => {
  try {
    const payload = {
      entity: "sts_lookups",
      data_value: "data_value",
      display_value: "display_value",
      filters: [
        {
          field: "lookup_category",
          value: LOOKUP_CATEGORY.ExpenseHead,
          condition: "eq",
        },
      ],
      ...(selectedExpenseHeadValue && {
        lookup_values: [selectedExpenseHeadValue],
        lookup_category: LOOKUP_CATEGORY.ExpenseHead,
      }),
    };
    const expenseHeadDDLList = await GLOBAL_DROP_DOWN(payload);
    if (
      expenseHeadDDLList &&
      expenseHeadDDLList.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      if (expenseHeadDDLList.data) {
        setExpenseHead(expenseHeadDDLList.data);
      } else {
        setExpenseHead([]);
      }
    } else {
      toastError(expenseHeadDDLList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    }
  } catch (e: any) {
    toastError(e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    handleError(e, "getExpenseHeadDdl");
  }
};
